import { DragEndEvent } from '@dnd-kit/core';
import { BlockStatus } from '@strength/domain';
import { MetricBenchmarkData } from '@strength/domain-workout';
import { useAtomValue } from 'jotai';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { Sheet, useSheetDndSensors } from '../../../components/sheet';
import { useMetrics } from '../../../hooks/useMetrics';
import {
  calculateSheetDimensions,
  getGridTemplateColumns,
} from '../grid-utils';
import {
  MiscMutationFns,
  WorkoutLayerMutationFns,
} from '../mutation-fns/types';
import { WorkoutPublishErrorsById } from '../publish/utils';
import { collapsedExercisesAtom, collapsedSectionsAtom } from '../slots/common';
import { getSlotsForBlock } from '../slots/workout';
import {
  BlockSelectionData,
  BlockView,
  WorkoutWithCycleAndSession,
} from '../types';

/**
 * Everything needed to drive an editable {@link Sheet} for a single workout
 * that is NOT specific to which workout is being edited. The base
 * (`BlockSheet`) and athlete (`AthleteSheet`) hosts each build one of these
 * — already pointed at the correct draft layer (`BlockDraftWorkoutLayer` vs
 * `AthleteDraftWorkoutLayer`) — and pass it down so the same Sheet engine and
 * slot generators that power Day/Week editing can be reused for one workout in
 * the All-view side panel. See SC-3416.
 */
export type SingleWorkoutEditContext = {
  allErrors: WorkoutPublishErrorsById[];
  mutationFns: WorkoutLayerMutationFns;
  miscMutationFns: MiscMutationFns;
  applyBenchmarkData: MetricBenchmarkData;
  hasBlockBeenPublished: boolean;
  isPublishDisabled: boolean;
  blockStatus: BlockStatus;
  allowFlexWorkouts: boolean;
  selectedAthleteId?: string;
  showActuals?: boolean;
  forceReadOnly?: boolean;
  getAssignedDate: (workout: WorkoutWithCycleAndSession) => string | null;
  onUndo: () => void;
  onRedo: () => void;
  onCopy: (data: readonly BlockSelectionData[]) => void;
  onPaste: (event: undefined, target: readonly BlockSelectionData[]) => void;
  onBulkClear: (data: readonly BlockSelectionData[]) => void;
  onDragEnd: (event: DragEndEvent) => void;
  addWorkout?: {
    create: (date: string) => WorkoutWithCycleAndSession | null;
    cancel: () => void;
  };
  getContextMenuContent?:
    | ((
        data: readonly BlockSelectionData[],
        miscMutationFns: MiscMutationFns,
      ) => ReactNode)
    | null;
  onDeleteWorkout?: (workoutId: string) => void;
  onCancelWorkout?: (workoutId: string) => void;
};

/**
 * Renders the existing block {@link Sheet} for exactly one workout.
 *
 * It drives {@link getSlotsForBlock} with a one-element workouts array and a
 * cycle-scoped {@link BlockView}, so the single workout matches the view and
 * spans the full grid width. This inherits the full editable surface (name
 * cell, date cell, exercises/sets/metrics, add-section/add-exercise, per-cell
 * publish-error decoration) without forking the Sheet.
 */
export function SingleWorkoutSheet({
  workout,
  editContext,
  onModalOrDrawerOpenChange,
}: {
  workout: WorkoutWithCycleAndSession;
  editContext: SingleWorkoutEditContext;
  onModalOrDrawerOpenChange?: (open: boolean) => void;
}) {
  const { getMetric } = useMetrics();
  const collapsedSections = useAtomValue(collapsedSectionsAtom);
  const collapsedExercises = useAtomValue(collapsedExercisesAtom);
  // SC-5486: same distance-constrained activation as the Day/Week sheets —
  // the section labels in this single-workout panel are draggable too.
  const sheetSensors = useSheetDndSensors();

  // The exercise picker and similar drawers opened from add-section /
  // add-exercise slots toggle this so Sheet keyboard/mouse handling pauses
  // while a drawer is open, mirroring the Day/Week sheets.
  const [isModalOrDrawerOpen, setIsModalOrDrawerOpen] = useState(false);
  const setModalOrDrawerOpen = useCallback(
    (open: boolean) => {
      setIsModalOrDrawerOpen(open);
      onModalOrDrawerOpenChange?.(open);
    },
    [onModalOrDrawerOpenChange],
  );

  const workouts = useMemo(() => [workout], [workout]);
  // Scope the view to this workout's cycle. With a single-element `workouts`
  // array this matches exactly one workout, which `getSlotsForBlock` then
  // renders at full grid width.
  const view: BlockView = useMemo(
    () => ({ mode: 'cycle', cycleId: workout.cycle.id }),
    [workout.cycle.id],
  );

  const {
    maxSetsColumns,
    maxNonSetsColumns,
    greatestExerciseHeightsInSections,
    maxMetricsPerExercise,
  } = useMemo(
    () =>
      calculateSheetDimensions(
        workouts,
        editContext.selectedAthleteId ?? null,
        editContext.blockStatus,
        { collapsedSections, collapsedExercises },
        editContext.showActuals,
      ),
    [
      workouts,
      editContext.selectedAthleteId,
      editContext.blockStatus,
      editContext.showActuals,
      collapsedSections,
      collapsedExercises,
    ],
  );

  const sheetProps = getSlotsForBlock({
    workouts,
    allErrors: editContext.allErrors,
    view,
    mutationFns: editContext.mutationFns,
    miscMutationFns: editContext.miscMutationFns,
    applyBenchmarkData: editContext.applyBenchmarkData,
    setModalOrDrawerOpen,
    hasBlockBeenPublished: editContext.hasBlockBeenPublished,
    isPublishDisabled: editContext.isPublishDisabled,
    blockStatus: editContext.blockStatus,
    allowFlexWorkouts: editContext.allowFlexWorkouts,
    getMetric,
    selectedWorkoutId: workout.workoutId,
    selectedAthleteId: editContext.selectedAthleteId,
    showActuals: editContext.showActuals,
    maxSetsColumns,
    maxNonSetsColumns,
    greatestExerciseHeightsInSections,
    getAssignedDate: editContext.getAssignedDate,
    forceReadOnly: editContext.forceReadOnly,
    // The panel has no session-management controls, so omit the workout-label
    // row (the workout name is edited via the in-grid name cell).
    hideWorkoutLabel: true,
  });

  const gridTemplateColumns = getGridTemplateColumns(
    workouts,
    maxNonSetsColumns,
    maxMetricsPerExercise,
    editContext.blockStatus,
    editContext.showActuals || editContext.forceReadOnly,
  );

  const { getContextMenuContent, miscMutationFns } = editContext;

  return (
    <Sheet
      {...sheetProps}
      sensors={sheetSensors}
      gridTemplateColumns={gridTemplateColumns}
      className="h-full overflow-auto"
      onUndo={editContext.onUndo}
      onRedo={editContext.onRedo}
      onCopy={editContext.onCopy}
      onPaste={editContext.onPaste}
      onBulkClear={editContext.onBulkClear}
      onDragEnd={editContext.onDragEnd}
      readOnly={editContext.forceReadOnly}
      disableKeyboardAndMouse={editContext.forceReadOnly || isModalOrDrawerOpen}
      getContextMenuContent={
        getContextMenuContent
          ? (data) => getContextMenuContent(data, miscMutationFns)
          : undefined
      }
    />
  );
}
